import pushGTMEvent from '@helpers/analyticsHelpers/pushGTMEvent';
import { getProduct, getPromotionProduct } from '@api/interfaces/productApi';
import { useSearchParams, useParams } from 'next/navigation';
import useSWR, { Fetcher } from 'swr';
import useLocalStorage from '@hooks/useLocalStorage';
import config from '@config';
import type { AxfoodOfflineProductViewModel, AxfoodProductDetailsViewModel } from '@occ/api-client';

const getProductCodeFromName = (name: string) => {
  const listOfName = name?.split('-');
  return listOfName[listOfName.length - 1].toUpperCase();
};

type keyType = {
  productCode: string;
  promotionStoreId: string;
  name: string;
};
const fetcher: Fetcher<AxfoodProductDetailsViewModel | AxfoodOfflineProductViewModel, keyType> = ({
  productCode,
  promotionStoreId,
  name,
}) => {
  const splitQuery = name.split('-');
  const isOfflinePromotionDetail = splitQuery[0] === 'offline';
  const isOnlinePromotionDetail = splitQuery[0] === 'online';

  if (isOfflinePromotionDetail || isOnlinePromotionDetail) {
    const promotionCode = splitQuery[splitQuery.length - 1];
    const promotionStoreIdOrDefault = promotionStoreId || '2110';

    if (isOfflinePromotionDetail) {
      return getPromotionProduct(promotionStoreIdOrDefault, promotionCode);
    }

    return getProduct(promotionCode);
  } else {
    const parsedProductCode = productCode || getProductCodeFromName(name);
    return getProduct(parsedProductCode);
  }
};

/**
 * Hook for fetching products using router query. It only fetches products
 * if there is a router.query.name present
 *
 * SWR is a strategy to first return the data from cache (stale),
 * then send the fetch request (revalidate), and finally come with the up-to-date data.
 */
const useProduct = () => {
  const searchParams = useSearchParams();
  const params = useParams<{ name?: string }>();
  const [promotionStoreId] = useLocalStorage(config.LOCAL_STORAGE_SELECTORS.promotionStoreId, '');

  // Search param takes priority (modal via ?name=...), fall back to path param (intercept route /produkt/[name])
  const name = searchParams?.get('name') || params?.name || null;
  const productCode = searchParams?.get('productCode');

  // conditionally fetch if name is router.query.name is available otherwise return null
  const key = name
    ? {
        endpoint: '/axfood/rest/c/{storeId}/{campaignCode} /axfood/rest/p/{productCode}',
        productCode,
        promotionStoreId,
        name,
      }
    : null;
  const { data: product, isLoading, error } = useSWR(key, fetcher);

  if (error) {
    pushGTMEvent({
      event: 'Track',
      category: 'error_page',
      action: 'product_not_found',
      label: window.location.pathname,
      value: 0,
    });
  }

  return {
    product,
    isLoading,
    error,
  };
};

export default useProduct;
